import React, { useState, ReactNode, useEffect, useMemo } from 'react';
import { 
  Grid, 
  IconButton, 
  Collapse,
  Tooltip,
  Box,
  Drawer,
  SxProps,
  Theme
} from '@mui/material';
import { 
  ChevronLeft, 
  ChevronRight,
  Close,
  // Close
} from '@mui/icons-material';
import { useLanguage } from '@erpsquad/common/runtime';
import { useTheme, useMediaQuery } from '@mui/material';

export interface ExpandableSummaryWrapperProps {
  children: ReactNode;
  defaultExpanded?: boolean;
  expandedGridSize?: number; // Grid size when expanded (1-12)
  collapsedGridSize?: number; // Grid size when collapsed (0-2)
  className?: string;
  ariaLabel?: string;
  onToggle?: (isExpanded: boolean, newGridSize: number) => void;
  persistState?: boolean;
  storageKey?: string;
  showToggleButton?: boolean;
  mobileDrawer?: boolean;
  showCollapseIcon?: boolean;
  sx?: SxProps<Theme>;
}

const ExpandableSummaryWrapper: React.FC<ExpandableSummaryWrapperProps> = ({
  children,
  defaultExpanded = true,
  expandedGridSize = 3,
  collapsedGridSize = 0,
  className = '',
  ariaLabel = 'Toggle summary panel',
  onToggle,
  persistState = false,
  storageKey = 'grid-summary-panel-state',
  showToggleButton = true,
  mobileDrawer = true,
  showCollapseIcon = true,
  sx: externalSx
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
const {currentLanguage}=useLanguage();
  const isRtl=useMemo(()=>currentLanguage?.direction==="rtl",[currentLanguage])
  // Initialize state with localStorage if persistState is enabled
  const getInitialState = () => {
    if (persistState && typeof window !== 'undefined') {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : defaultExpanded;
    }
    return defaultExpanded;
  };

  const [isExpanded, setIsExpanded] = useState<boolean>(getInitialState);
  // Add this state to your component
  const [showAfterDelay, setShowAfterDelay] = useState(false);

  // Add this useEffect
  useEffect(() => {
    if(!isExpanded) {
      setShowAfterDelay(false)
       return
    }
    const timer = setTimeout(() => {
      setShowAfterDelay(true);
    }, 250);

    return () => clearTimeout(timer);
  }, [isExpanded]);

  const handleToggle = () => {
    const newState = !isExpanded;
    const newGridSize = newState ? expandedGridSize : collapsedGridSize;
    setIsExpanded(newState);
    
    // Persist state if enabled
    if (persistState && typeof window !== 'undefined') {
      localStorage.setItem(storageKey, JSON.stringify(newState));
    }
    
    // Call external toggle handler with new grid size
    onToggle?.(newState, newGridSize);
  };

  const currentGridSize = isExpanded ? expandedGridSize : collapsedGridSize;

  // Mobile drawer implementation
  if (isMobile && mobileDrawer) {
    return (
      <>
        {/* Mobile Toggle Button */}
        {showToggleButton && (
          <IconButton
            onClick={handleToggle}
            sx={{
              position: 'fixed',
              top: '50%',
              [isRtl ? 'left' : 'right']: 0,
              zIndex: theme.zIndex.fab,
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '8px 0 0 8px',
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              },
            }}
            aria-label={ariaLabel}
          >
            <ChevronLeft />
          </IconButton>
        )}

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={isExpanded}
          onClose={handleToggle}
          sx={(theme) => ({
            '& .MuiDrawer-paper': {
              width: '90vw',
              [theme.breakpoints.up('md')]: {
                maxWidth: '400px',
                marginTop: '65px',
              },
              [theme.breakpoints.up('sm')]: {
                maxWidth: '300px',
                marginTop: '65px',
              },
              [theme.breakpoints.up('xs')]: {
                maxWidth: '200px',
                marginTop: '56px',
              }
            },
          })}
        >
         {children}
        </Drawer>
      </>
    );
  }

  // Desktop/Tablet Grid implementation
  return (
    <Grid 
      item 
      xs={currentGridSize} 
      sx={{
        position: 'relative',
        transition: theme.transitions.create(['flex-basis', 'max-width'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        overflow: 'visible',
        minHeight: '100%',
        // Ensure grid item is visible even when size is 0
        display: currentGridSize === 0 ? 'block' : 'block',
        minWidth: currentGridSize === 0 ? 0 : 'auto',
        ...(externalSx as object),
      }}
      className={className}
    >
      {/* Alternative toggle button for completely collapsed state */}
      {(showToggleButton && !isExpanded) && (
        <Tooltip title="Expand summary" placement="left">
          <IconButton
            onClick={handleToggle}
            sx={{
              position: 'fixed',
              top: '20%',
              [isRtl ? 'left' : 'right']: 0,
              width:'30px',
              zIndex: theme.zIndex.fab,
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '8px 0 0 8px',
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              },
            }}
            aria-label="Expand summary panel"
          >
            {!isExpanded ? <ChevronLeft /> : <ChevronRight />}
          </IconButton>
        </Tooltip>
      )}

      {/* Content Container */}
      {(currentGridSize > 0 && showAfterDelay) && (
        <Box
          sx={{
            height: '100%',
            width: '100%',
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: theme.palette.background.paper,
            borderLeft: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px 0 0 8px',
            position: 'relative',
          }}
        >
          {/* Fixed Collapse Icon at Top of Summary */}
          {showCollapseIcon && isExpanded && (
            <Tooltip title="Close summary" placement="left">
              <IconButton
                onClick={handleToggle}
                size='small'
                sx={{
                  position: 'absolute',
                  top: '-15px',
                  [isRtl ? 'right' : 'left']: '-15px',
                  zIndex: 1200,
                  bgcolor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '50%',
                  '&:hover': {
                    bgcolor: '#fff',
                  },
                  boxShadow: 'none',
                }}
              >
                <Close sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}

          <Collapse 
            in={isExpanded} 
            orientation="horizontal" 
            sx={{ 
              height: '100%',
              '& .MuiCollapse-wrapperInner': {
                height: '100%',
                width: '100%',
              },
              width: '100%',
            }}
          >
            <Box sx={{ 
              width: '100%', 
              height: '100%', 
              overflow: 'auto',
              minWidth: 0,
              // pt: showCollapseIcon ? 6 : 0,
            }}>
              {children}
            </Box>
          </Collapse>

          {/* Collapsed State Indicator for partial collapse */}
          {!isExpanded && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                width: '100%',
                color: theme.palette.text.secondary,
                cursor: 'pointer',
              }}
              onClick={handleToggle}
            >
              <Box
                sx={{
                  writingMode: 'vertical-lr',
                  textOrientation: 'mixed',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  letterSpacing: '0.1em',
                }}
              >
                SUMMARY
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Grid>
  );
};

export default ExpandableSummaryWrapper;
